import { useEffect, useRef, type JSX, type ReactNode } from 'react';
import gsap from 'gsap';
import { Spread } from '../shared/components/Spread.js';
import { prefersReducedMotion } from '../preferences/reducedMotion.js';
import './GameEndBurnCanvas.css';

interface GameEndBurnCanvasProps {
  versoContent?: ReactNode;
  rectoContent?: ReactNode;
  onConsumed?: () => void;
  onComplete: () => void;
}

/*
 * The ghost-win burn — canvas-2D particle implementation. Picks up the
 * "Lane 4" direction from docs/handoffs/game-end-ghost-burn-followup.md and
 * faithfully ports the canvas-mockup particle physics
 * (docs/mockups/game-end-burn-canvas.html). ONE-BOOK pattern: the prior
 * phase's open spread is consumed by Spectral fire whose particles ARE the
 * otherworldly. The resting state (GhostVoidScene) lives behind this overlay;
 * at onConsumed the void mounts under the still-fading particle stage so the
 * reveal emerges through the dissipation rather than popping after.
 *
 * Choreography (~2.70s) — single convergence point at 1500ms, matching
 * the mockup's beat where book-gone / emission-peak-end / void-reveal all
 * land on the same instant:
 *   - 0–1500ms : Particles erupt from the book's mid-to-bottom band and
 *                rise. Spawn density ramps fast (0–400ms ease-in), holds at
 *                peak. The book runs ONE coordinated GSAP tween over
 *                0.35–1.50s: tilt -2°, drop 14px, scale 0.84, opacity 1→0
 *                — all on a single power2.in curve so they finish together.
 *                NO brightness/darkening filter on the book; every variant
 *                of "darken then fade" produced a "content peeks back
 *                through" read mid-fade. The canvas particles carry the
 *                BURN look; the book just transforms + fades cleanly.
 *   - 1500ms   : THE PIVOT. Book opacity hits 0 exactly here. onConsumed
 *                fires → caller mounts GhostVoidScene underneath (which
 *                deliberately renders solid, no fade-in, to avoid leaking
 *                the PageFrame bg as a phantom book-rectangle
 *                outline — see GhostVoidScene.css). Particle emission
 *                begins its taper.
 *   - 1500–2100ms: Spawn density tapers to 0 (mockup's 600ms window). As
 *                  particle density thins, more of the void shows through
 *                  the canvas's transparent gaps. The void's reveal IS the
 *                  particles thinning, not a separate fade — exactly the
 *                  mockup's mechanism.
 *   - 2100–4000ms: No new emission; remaining particles drift up + fade
 *                  via their own life-curve alpha decay. Canvas thins
 *                  naturally to nothing. The 4000ms tail is sized so the
 *                  slowest particle (spawned right at the 2.10s taper end
 *                  with a 1.9s maxLife) completes its natural fade rather
 *                  than getting truncated when the parent unmounts.
 *   - 4000ms   : onComplete fires.
 *
 * Why canvas (vs the CSS+SVG-filter approach this replaces): the browser's
 * DOM/CSS compositor isn't built for many small moving elements with blending.
 * The SVG `feTurbulence + feDisplacementMap` on the wisp gradients re-evaluated
 * the filter every frame, GPU-thrashing mid-range Firefox. Canvas-2D with
 * `globalCompositeOperation = 'lighter'` is ONE compositor layer (the canvas
 * bitmap), regardless of particle count, so 280 particles ride smoothly. The
 * hot-core glow that emerges from overlapping additively-blended particles
 * is the *physical* behaviour of real flame, which the gradient stack could
 * only fake.
 *
 * Reduced motion: skip entirely; onConsumed + onComplete fire next tick so
 * only the void renders, no cinematic.
 */
export function GameEndBurnCanvas({
  versoContent,
  rectoContent,
  onConsumed,
  onComplete,
}: GameEndBurnCanvasProps): JSX.Element {
  const stageRef = useRef<HTMLDivElement>(null);
  const bookRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  /* Callbacks live in refs so the mount effect's dependency array stays
     empty — the cinematic must run EXACTLY ONCE per mount. If callbacks
     were direct deps, a parent passing inline arrows would re-fire this
     effect every render (the first onConsumed call already triggers one,
     since it sets parent state), and a second emission wave would play
     on top of the first. The parent (GameEnd.tsx) also useCallback's these
     for prop stability, but this layer guarantees correctness regardless. */
  const onConsumedRef = useRef(onConsumed);
  const onCompleteRef = useRef(onComplete);
  onConsumedRef.current = onConsumed;
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const reduced = prefersReducedMotion();
    const stage = stageRef.current;
    const book = bookRef.current;
    const canvas = canvasRef.current;
    if (reduced || !stage || !book || !canvas) {
      onConsumedRef.current?.();
      onCompleteRef.current();
      return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      onConsumedRef.current?.();
      onCompleteRef.current();
      return;
    }

    /* ── Canvas sizing (DPR-aware) ──────────────────────────────────────
       Resize syncs the backing-store pixel grid to the device pixel ratio
       so particles render crisp at HiDPI. Drawn in CSS pixels via the ctx
       scale; re-run on viewport resize. */
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let cssWidth = 0;
    let cssHeight = 0;
    const resize = (): void => {
      const rect = canvas.getBoundingClientRect();
      cssWidth = rect.width;
      cssHeight = rect.height;
      canvas.width = Math.max(1, Math.round(cssWidth * dpr));
      canvas.height = Math.max(1, Math.round(cssHeight * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    /* ── Cool Spectral palette ──────────────────────────────────────────
       Four tones, picked at RANDOM per particle so the column reads as
       organic tonal variance rather than a structured banded gradient.
       Additive overlap (`globalCompositeOperation = 'lighter'`) pushes
       overlapping bright tones toward white — the hot-core glow. */
    const PALETTE: ReadonlyArray<string> = [
      'oklch(94% 0.06 228 / ALPHA)', // pale hot core
      'oklch(86% 0.10 230 / ALPHA)', // bright silver-blue
      'oklch(78% 0.08 232 / ALPHA)', // mid spectral
      'oklch(64% 0.07 235 / ALPHA)', // deeper cool
    ];

    /* ── Particle pool ──────────────────────────────────────────────────
       Fixed-size pool, recycled when a particle's `life` reaches 0. Single
       particle class (the mockup's; the embers ARE the regular particles
       drifting on their lateral velocity past the book bounds). Object
       reuse avoids GC churn over a ~2.4s loop. */
    interface Particle {
      alive: boolean;
      x: number;
      y: number;
      vx: number;        // base lateral velocity (px/sec)
      vy: number;        // vertical velocity (px/sec); negative = upward
      life: number;      // seconds remaining
      maxLife: number;   // seconds total
      size: number;      // CSS px (peak radius)
      hueIdx: number;    // index into PALETTE
      wobblePhase: number;  // 0..2π — per-particle phase offset for the
                            //  sinusoidal lateral wobble (flame-flicker)
      wobbleAmp: number;    // px/sec — peak lateral wobble velocity
    }
    const POOL_SIZE = 280;
    const particles: Particle[] = Array.from({ length: POOL_SIZE }, () => ({
      alive: false, x: 0, y: 0, vx: 0, vy: 0,
      life: 0, maxLife: 0, size: 0, hueIdx: 0,
      wobblePhase: 0, wobbleAmp: 0,
    }));

    /* ── Spawn ──────────────────────────────────────────────────────────
       Region is computed per-frame from the book's *current* bounding rect
       (the GSAP timeline is shrinking + dropping the book underneath, so
       the spawn band tracks it). Mid-to-bottom 30% × 90% width. Random Y
       inside the band so spawn isn't a single line; random tone + size from
       the mockup. Velocity is the hybrid pass: slower base vy than the
       mockup's wide 40–130 fountain (here 22–55 px/sec) so the rise reads
       as *controlled* rather than jetted, plus per-particle wobble that
       gets applied each tick to give the column flame-flicker character
       instead of mechanical parallel streaks. */
    const spawn = (p: Particle, region: { x: number; y: number; w: number; h: number }): void => {
      p.alive = true;
      p.x = region.x + (Math.random() - 0.5) * region.w;
      p.y = region.y + (Math.random() - 0.5) * region.h * 0.4;
      /* Velocity tuned so a fast particle (vy=-160, life=1.9s, accel=80
         px/sec²) climbs ~450px — past the top of a 660px book and into
         the cooled desk above (the "fire takes the room" read). A slow
         particle (vy=-60, life=0.9s) only climbs ~85px so it barely lifts
         off the page. The spread between "barely lifts" and "streaks past
         the book" gives the column its layered look. Lifetimes match the
         mockup (0.9–1.9s) — earlier I overshot to 1.4–2.6s which left
         particles hanging around well past the void reveal. */
      p.vy = -(60 + Math.random() * 100);          // 60–160 px/sec upward
      p.vx = (Math.random() - 0.5) * 22;           // ±11 px/sec lateral base drift
      p.maxLife = 0.9 + Math.random() * 1.0;       // 0.9–1.9 s (mockup)
      p.life = p.maxLife;
      p.size = 14 + Math.random() * 28;            // 14–42 px peak radius (mockup)
      p.hueIdx = Math.floor(Math.random() * PALETTE.length);
      p.wobblePhase = Math.random() * Math.PI * 2;
      p.wobbleAmp = 18 + Math.random() * 16;       // 18–34 px/sec peak wobble
    };

    /* ── Book timeline ─────────────────────────────────────────────────
       Single coordinated tween: tilt + drop + scale + opacity ride one
       power2.in curve over 0.35–1.50s. They finish together at 1.50s —
       the pivot moment where the void mounts underneath and emission
       begins tapering. NO darkening filter — earlier passes ran
       brightness/multiply-overlay darkening together with the fade and
       every variant produced a "content peeks back through" mid-fade
       read (the eye perceives partial-opacity-on-partial-darken as the
       content briefly returning). With no darkening, the book just
       transforms + fades cleanly; the canvas particles are what give the
       BURN look. */
    const tl = gsap.timeline({
      onComplete: () => { onCompleteRef.current(); },
    });
    tl.to(
      book,
      {
        opacity: 0,
        scale: 0.84,
        y: 14,
        rotation: -2,
        duration: 1.15,
        ease: 'power2.in',
      },
      0.35,
    );
    /* THE PIVOT (1.50s). Book is gone. Void mounts underneath, deliberately
       solid (no fade-in — GhostVoidScene.css explains why: a fading void
       leaks the PageFrame bg through as a phantom book-rectangle
       outline). From this instant, the canvas's own particle taper +
       per-particle alpha decay carry the dissipation reveal — the void
       becomes visible *through the thinning particles*, exactly the mockup's
       mechanism. No stage-opacity fade is layered on top; doing so would
       reintroduce the same phantom-rectangle leak the void renders solid
       to prevent. */
    tl.call(() => { onConsumedRef.current?.(); }, [], 1.50);
    /* Hold the timeline open until the LATEST particles can die naturally.
       Last spawn fires around 2.10s (emission taper end); the slowest
       maxLife is 1.9s, so the most stubborn ember lives until ~4.00s. If
       onComplete fired earlier, the parent would unmount this component
       and the cleanup would cancel rAF, snapping mid-life embers to zero
       — that was the previous "hard cut-off" the user noticed. Letting the
       timeline ride to 4.00s lets each particle complete its life-curve
       alpha decay, so the fade-out is genuinely natural like the mockup. */
    tl.to({}, { duration: 2.50 }, 1.50);

    /* ── rAF loop ───────────────────────────────────────────────────────
       Physics: dt in seconds (clamped to 40ms so a tab restore doesn't
       catapult the sim), per-particle upward acceleration (heat curl,
       hybrid pass over the mockup's smoke-decel), per-particle sinusoidal
       wobble (flame-flicker). Spawn density envelope: ramp-in 0–400ms →
       peak through 1500ms (pivot) → taper to 0 by 2100ms.

       Loop exits when (elapsed >= SIM_END_MS) AND no particles still alive.
       That second condition lets late-spawned particles finish their
       lifetime naturally instead of being cut off — a particle spawned
       right at the 1.5s emission cutoff with a 1.9s maxLife runs to ~3.4s,
       and we let it. The GSAP stage-opacity fade is independent so the
       canvas drains visually regardless. */
    const startMs = performance.now();
    const EMIT_PEAK_MS = 1500;  // peak emission ends at the pivot (mockup)
    const EMIT_TAPER_MS = 2100; // emission fully stops 600ms after pivot (mockup)
    const SIM_END_MS = 4000;    // last-spawned slow-life particle dies ~4.0s; sim runs until then so the natural alpha decay isn't truncated
    let last = startMs;
    let rafId = 0;

    const step = (now: number): void => {
      const dt = Math.min(0.04, (now - last) / 1000);
      last = now;
      const elapsedMs = now - startMs;
      const elapsed = elapsedMs / 1000;

      /* Spawn density envelope: ramp up over 400ms, hold through 1500ms,
         taper down to 0 by 2100ms. `toSpawn` is capped at 8/frame so a
         dropped frame can't burst-fill the pool. */
      let intensity: number;
      if (elapsedMs < EMIT_PEAK_MS) {
        intensity = Math.min(1, elapsed / 0.4);
      } else if (elapsedMs < EMIT_TAPER_MS) {
        intensity = Math.max(0, 1 - (elapsedMs - EMIT_PEAK_MS) / (EMIT_TAPER_MS - EMIT_PEAK_MS));
      } else {
        intensity = 0;
      }
      const toSpawn = Math.round(intensity * 8);

      /* Spawn region tracks the book's CURRENT bounding rect (GSAP is
         shrinking + dropping it under our feet; the fire stays anchored to
         where the paper actually is, not to where it started). Mid-to-bottom
         30% band × 90% width. */
      const bRect = book.getBoundingClientRect();
      const sRect = stage.getBoundingClientRect();
      const region = {
        x: bRect.left + bRect.width / 2 - sRect.left,
        y: bRect.top + bRect.height * 0.6 - sRect.top,
        w: bRect.width * 0.9,
        h: bRect.height * 0.3,
      };
      let spawned = 0;
      for (let i = 0; i < particles.length && spawned < toSpawn; i += 1) {
        const p = particles[i];
        if (p && !p.alive) { spawn(p, region); spawned += 1; }
      }

      /* Advance + cull. Hybrid travel:
         - vy accelerates upward over life (heat rises faster as it climbs)
           instead of the mockup's smoke-decel. Reads as flame, not smoke.
         - Lateral position gets a per-particle sinusoidal wobble added to
           its base vx drift — this is what gives each particle a
           flame-flicker path instead of a straight-line ascent. The wobble
           frequency rides on the particle's age so wobble speed varies. */
      for (const p of particles) {
        if (!p.alive) continue;
        p.life -= dt;
        if (p.life <= 0) { p.alive = false; continue; }
        p.vy -= 80 * dt; // upward acceleration — heat curl carries the fastest particles past the book's top into the desk
        const age = p.maxLife - p.life;
        const wobble = Math.sin(age * 7.5 + p.wobblePhase) * p.wobbleAmp;
        p.x += (p.vx + wobble) * dt;
        p.y += p.vy * dt;
      }

      /* Render. */
      ctx.clearRect(0, 0, cssWidth, cssHeight);
      ctx.globalCompositeOperation = 'lighter';
      for (const p of particles) {
        if (!p.alive) continue;
        const lifeRatio = p.life / p.maxLife;
        const alpha = lifeRatio * 0.7;
        const r = p.size * (0.6 + 0.4 * lifeRatio);
        const color = PALETTE[p.hueIdx] ?? PALETTE[2]!;
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
        grad.addColorStop(0, color.replace('ALPHA', String(alpha)));
        grad.addColorStop(0.5, color.replace('ALPHA', String(alpha * 0.4)));
        grad.addColorStop(1, color.replace('ALPHA', '0'));
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalCompositeOperation = 'source-over';

      /* Keep ticking until the sim cutoff AND no particle still alive. */
      const anyAlive = particles.some((p) => p.alive);
      if (elapsedMs < SIM_END_MS || anyAlive) {
        rafId = requestAnimationFrame(step);
      } else {
        ctx.clearRect(0, 0, cssWidth, cssHeight);
      }
    };
    rafId = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
      tl.kill();
    };
    /* Empty deps — the cinematic runs EXACTLY ONCE per mount. Callback
       updates are picked up via the refs above (see onConsumedRef comment).
       Without this, an inline-arrow parent prop would re-fire the effect on
       every render and play a second emission wave on top of the first. */
  }, []);

  return (
    <div className="susdle-gameend-burn-canvas" ref={stageRef} aria-hidden>
      <div className="susdle-gameend-burn-canvas__book" ref={bookRef}>
        <Spread pov="ghost" verso={versoContent} recto={rectoContent} />
      </div>
      <canvas
        className="susdle-gameend-burn-canvas__canvas"
        ref={canvasRef}
        aria-hidden
      />
    </div>
  );
}
